/* eslint-disable no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import axios, { AxiosResponse } from 'axios';
import {
    createContext,
    type Dispatch,
    type PropsWithChildren,
    type SetStateAction,
    useEffect,
    useState
} from 'react';

import * as authHelper from '../_helpers';
import { type AuthModel, type UserModel } from '@/auth';
import { enqueueSnackbar } from 'notistack';

const API_URL = import.meta.env.VITE_APP_API_URL;

export const GET_USER_BY_ACCESSTOKEN_URL = `${API_URL}/user`;
export const LOGIN_URL = `${API_URL}/login`;
export const REGISTER_URL = `${API_URL}/register`;
export const REQUEST_PASSWORD_URL = `${API_URL}/request-password`;

interface AuthContextProps {
    isLoading: boolean;
    auth: AuthModel | undefined;
    saveAuth: (auth: AuthModel | undefined) => void;
    currentUser: UserModel | undefined;
    setCurrentUser: Dispatch<SetStateAction<UserModel | undefined>>;
    login: (email: string, password: string) => Promise<void>;
    loginWithGoogle?: () => Promise<void>;
    loginWithFacebook?: () => Promise<void>;
    loginWithGithub?: () => Promise<void>;
    register: (
        email: string,
        password: string,
        password_confirmation: string,
        first_name: string,
        last_name: string,
        company: string,
        country: string,
        accept_terms: boolean,
        accept_data_protection: boolean
    ) => Promise<boolean>;
    requestPassword: (email: string) => Promise<boolean>;
    getUser: () => Promise<AxiosResponse<any>>;
    logout: () => void;
    verify: () => Promise<void>;
    impersonate: (p_id: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextProps | null>(null);

const AuthProvider = ({ children }: PropsWithChildren) => {
    const [loading, setLoading] = useState(true);
    const [auth, setAuth] = useState<AuthModel | undefined>(authHelper.getAuth());
    const [currentUser, setCurrentUser] = useState<UserModel | undefined>();

    const verify = async () => {
        if (auth) {
            try {
                const { data: user } = await getUser();
                setCurrentUser(user);
            } catch (error) {
                saveAuth(undefined);
                setCurrentUser(undefined);
            }
        }
    };

    useEffect(() => {
        verify().finally(() => {
            setLoading(false);
        });
    }, []);

    const saveAuth = (auth: AuthModel | undefined) => {
        setAuth(auth);
        if (auth) {
            authHelper.setAuth(auth);
        } else {
            authHelper.removeAuth();
        }
    };

    const login = async (email: string, password: string) => {
        try {
            const { data: auth } = await axios.post<AuthModel>(LOGIN_URL, { email, password });
            saveAuth(auth);
            const { data: user } = await getUser();
            setCurrentUser(user);
        } catch (err: any) {
            saveAuth(undefined);
            if (err.response && err.response.status === 401) {
                enqueueSnackbar('Invalid credentials. Please check your email and password.', {
                    variant: 'solid',
                    state: 'danger'
                });
            } else {
                enqueueSnackbar(`Error: ${err.message ?? err}`, {
                    variant: 'solid',
                    state: 'danger'
                });
            }
        }
    };

    const register = async (
        email: string,
        password: string,
        password_confirmation: string,
        first_name: string,
        last_name: string,
        company: string,
        country: string,
        accept_terms: boolean,
        accept_data_protection: boolean
    ): Promise<boolean> => {
        try {
            const { data: auth } = await axios.post(REGISTER_URL, {
                email,
                password,
                password_confirmation,
                first_name,
                last_name,
                company,
                country,
                accept_terms,
                accept_data_protection
            });
            if (auth && auth.access_token) {
                saveAuth(auth);
                const { data: user } = await getUser();
                setCurrentUser(user);
                return true;
            } else {
                return false;
            }
        } catch (error) {
            saveAuth(undefined);
            if (axios.isAxiosError(error) && error.response) {
                const errorMessage = error.response.data?.detail || 'An error occurred during registration.';
                enqueueSnackbar(errorMessage, {
                    variant: 'solid',
                    state: 'danger'
                });
            } else {
                enqueueSnackbar('An unexpected error occurred.', {
                    variant: 'solid',
                    state: 'danger'
                });
            }
            return false;
        }
    };

    const requestPassword = async (email: string): Promise<boolean> => {
        try {
            const { data } = await axios.post<{ result: boolean; message: string }>(
                REQUEST_PASSWORD_URL,
                { email }
            );

            enqueueSnackbar(data.message, {
                variant: 'solid',
                state: data.result ? 'success' : 'danger',
            });

            return data.result;
        } catch (error) {
            if (axios.isAxiosError(error) && error.response) {
                const errorMessage = error.response.data?.detail || 'An error occurred.';
                enqueueSnackbar(errorMessage, {
                    variant: 'solid',
                    state: 'danger',
                });
            } else {
                enqueueSnackbar('An unexpected error occurred. Please try again later.', {
                    variant: 'solid',
                    state: 'danger',
                });
            }

            return false;
        }
    };

    const getUser = async () => {
        return await axios.get<UserModel>(GET_USER_BY_ACCESSTOKEN_URL);
    };

    const logout = () => {
        saveAuth(undefined);
        setCurrentUser(undefined);
    };

    const impersonate = async (p_id: string) => {
        try {
            const { data: auth } = await axios.post<AuthModel>(`${API_URL}/admin/impersonate`, { p_id }, {
                headers: { 'Content-Type': 'application/json' }
            });

            saveAuth(auth);
            const { data: user } = await getUser();
            setCurrentUser(user);

            window.location.href = '/';
        } catch (error) {
            enqueueSnackbar('Failed to impersonate user.', {
                variant: 'solid',
                state: 'danger',
            });
            console.error(error);
        }
    };

    return (
        <AuthContext.Provider
            value={{
                isLoading: loading,
                auth,
                saveAuth,
                currentUser,
                setCurrentUser,
                login,
                register,
                requestPassword,
                getUser,
                logout,
                verify,
                impersonate
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export { AuthContext, AuthProvider };
